import QRCode from "react-qr-code";
import { useState, useEffect } from "react";
import { Button } from "@mui/material";
import UploadIcon from "@mui/icons-material/Upload";
import { Typography } from "@mui/material";

import excelToUUID from "./excelToUUID";
import ExtractedDataTable from "./ExcelExtractedDataTable";
import { ExcelData } from "./ExcelData";

interface QrCodeGeneratorNanesaProps {
  onQrCodeGenerated?: (url: string, extractedData: any) => void;
  onPdfFilesChange?: (savedPdf: { coa?: File; sds?: File; ddt?: File }) => void;
}

const QrCodeGeneratorNanesa: React.FC<QrCodeGeneratorNanesaProps> = ({ onQrCodeGenerated, onPdfFilesChange }) => {
  const [urlValue, setUrlValue] = useState(""); // Stores the QR Code URL
  const [excelData, setExcelData] = useState<ExcelData | null>(null); // Stores the extracted Excel data
  const [selectedFile, setSelectedFile] = useState<File | null>(null); // Stores the selected file
  type PdfFiles = { coa?: File; sds?: File; ddt?: File };
  const [savedPdf, setSavedPdf] = useState<PdfFiles>({});

  // Replace always the previous saved PDF files with the new ones
  useEffect(() => {
    if (onPdfFilesChange) onPdfFilesChange(savedPdf);
  }, [savedPdf, onPdfFilesChange]);

  const handlePdfChange = (type: "coa" | "sds" | "ddt", file: File | null) => {
    setSavedPdf((prev) => ({ ...prev, [type]: file || undefined }));
  };

  // Use the selected file to generate the QR code
  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setSelectedFile(file);

      excelToUUID(
        file,
        ({ uuid, url, extractedData }) => {
          setUrlValue(url);
          setExcelData(extractedData);

          if (onQrCodeGenerated) {
            onQrCodeGenerated(url, extractedData);
          }
        },
        (error) => {
          console.error("Error processing Excel file:", error);
        }
      );
    }
  };

  return (
    <div>
      {/* QR Code */}
      {urlValue && (
        <div
          id={"qr-code"}
          style={{
            height: "auto",
            margin: "0 auto",
            maxWidth: 256,
            width: "100%",
            background: "white",
            padding: "16px",
            borderRadius: "10px",
          }}
        >
          <QRCode size={256} style={{ height: "auto", maxWidth: "100%", width: "100%" }} value={urlValue} />
        </div>
      )}

      {/* PDF Upload Section */}
      {excelData && (
        <div style={{ display: "flex", justifyContent: "center", gap: 16, margin: "16px 0" }}>
          {(["coa", "ddt", "sds"] as const).map((type) => {
            const isDisabled = (type === "coa" && excelData.coa === "N/A") || (type === "ddt" && excelData.numeroDdt === "N/A") || (type === "sds" && excelData.sds === "N/A");

            return (
              <label key={type} style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                <input type="file" accept="application/pdf" style={{ display: "none" }} onChange={(e) => handlePdfChange(type, e.target.files?.[0] || null)} disabled={isDisabled} />
                <Button variant={isDisabled ? "outlined" : "contained"} component="span" disabled={isDisabled}>
                  Seleziona {type.toUpperCase()}
                </Button>
                {savedPdf[type] && (
                  <Typography variant="caption" sx={{ padding: 1 }}>
                    {savedPdf[type]?.name}
                  </Typography>
                )}
              </label>
            );
          })}
        </div>
      )}

      {/* File Upload */}
      <div>
        <div style={{ marginTop: "20px", display: "flex", justifyContent: "center" }}>
          {!selectedFile ?
            <>
              <input type="file" accept=".xlsx, .xls" onChange={handleFileUpload} style={{ display: "none" }} id="file-upload" />
              <label htmlFor="file-upload">
                <Button startIcon={<UploadIcon />} variant="contained" component="span">
                  Seleziona XLSX
                </Button>
              </label>
            </>
          : null}
        </div>
      </div>

      {/* Display Extracted Data */}
      {excelData && <ExtractedDataTable excelData={excelData} />}
    </div>
  );
};

export default QrCodeGeneratorNanesa;
